import React, { useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa6";
import { IoSettings } from "react-icons/io5";
import SlippageSettings from "./SlippageSettings";
import { useParams, useNavigate } from "react-router-dom";
import { useActiveAccount, useActiveWalletChain } from "thirdweb/react";
import {
    getErc20Contract,
    getLPContract,
    getRouterContract,
    ThirdWebClient,
} from "../contexts/thirdWebClient";
import {
    allowance,
    approve,
    balanceOf,
    getCurrencyMetadata,
} from "thirdweb/extensions/erc20";
import {
    prepareContractCall,
    readContract,
    sendTransaction as sendTx,
    waitForReceipt,
} from "thirdweb";
import { ethers } from "ethers";
import { routerAddress } from "../contexts/config";
import { toast } from "react-toastify";
import { list } from "../contexts/SelectTokenDATA";
import { safeFixedString } from "../utility/formatNumber";

// Helper to find token icon from the token list by symbol
const getTokenIcon = (symbol) => {
    const token = list.find(
        (t) => t.symbol?.toLowerCase() === symbol?.toLowerCase()
    );
    return token || null;
};

const TokenIcon = ({ symbol, size = "20px" }) => {
    const tokenData = getTokenIcon(symbol);
    if (tokenData?.emoji) {
        return (
            <span className={`flex items-center justify-center`} style={{ width: size, height: size, fontSize: size }}>
                {tokenData.emoji}
            </span>
        );
    }
    if (tokenData?.img) {
        return <img src={tokenData.img} alt={symbol} className="rounded-full" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-perpx-cyan/20 flex items-center justify-center text-xs text-perpx-text-primary" style={{ width: size, height: size }}>
            {symbol?.charAt(0)}
        </div>
    );
};

const RemoveLiquidity = () => {
    const navigate = useNavigate();
    const [value, setValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [slippage, setSlippage] = useState(() => {
        const saved = localStorage.getItem("perpx_slippage");
        return saved ? parseFloat(saved) : 1;
    });

    const { pairAddress } = useParams();

    const add = useActiveAccount();
    const [address, setAddress] = useState("");
    const chain = useActiveWalletChain();

    useEffect(() => {
        if (add && add.address && chain && pairAddress) {
            setAddress(add.address);
            fetchLpDetails(pairAddress, add.address, chain);
        }
    }, [add, chain, pairAddress]);

    const [lpDetails, setLpDetails] = useState({});

    const fetchLpDetails = async (pairAddress, address, chain) => {
        try {
            setDataLoading(true);
            const contract = getErc20Contract(pairAddress, chain);
            let currencyMetadata = await getCurrencyMetadata({ contract });
            let pairTotalSupply = await readContract({
                contract,
                method: "function totalSupply() view returns (uint256)",
                params: [],
            });
            let userBalance = await balanceOf({
                contract,
                address: address,
            });
            let approvedAmount = await allowance({
                contract,
                owner: address,
                spender: routerAddress,
            });

            const lpContract = getLPContract(pairAddress, chain);

            let token0Address = await readContract({
                contract: lpContract,
                method: "function token0() view returns (address)",
                params: [],
            });
            let token1Address = await readContract({
                contract: lpContract,
                method: "function token1() view returns (address)",
                params: [],
            });

            const Token0contract = getErc20Contract(token0Address, chain);

            let tokenOMetaData = await getCurrencyMetadata({
                contract: Token0contract,
            });
            let tokenOUserBalance = await balanceOf({
                contract: Token0contract,
                address: pairAddress,
            });

            const Token1contract = getErc20Contract(token1Address, chain);
            let token1MetaData = await getCurrencyMetadata({
                contract: Token1contract,
            });
            let token1UserBalance = await balanceOf({
                contract: Token1contract,
                address: pairAddress,
            });

            let userShare =
                (Number(
                    ethers.formatUnits(userBalance, currencyMetadata?.decimals),
                ) *
                    100) /
                Number(
                    ethers.formatUnits(
                        pairTotalSupply,
                        currencyMetadata?.decimals,
                    ),
                );

            const routerContract = await getRouterContract(chain);
            let token0Price = await readContract({
                contract: routerContract,
                method: "function quote(uint256 amountA, uint256 reserveA, uint256 reserveB) pure returns (uint256 amountB)",
                params: [
                    ethers.parseUnits("1", tokenOMetaData?.decimals),
                    tokenOUserBalance,
                    token1UserBalance,
                ],
            });
            let token1Price = await readContract({
                contract: routerContract,
                method: "function quote(uint256 amountA, uint256 reserveA, uint256 reserveB) pure returns (uint256 amountB)",
                params: [
                    ethers.parseUnits("1", token1MetaData?.decimals),
                    token1UserBalance,
                    tokenOUserBalance,
                ],
            });

            let pairData = {
                pairAddress: pairAddress,
                token0Address,
                token1Address,
                currencyMetadata,
                token0Price: Number(
                    ethers.formatUnits(token0Price, token1MetaData?.decimals),
                ),
                token1Price: Number(
                    ethers.formatUnits(token1Price, tokenOMetaData?.decimals),
                ),
                lpName:
                    tokenOMetaData?.symbol +
                    "-" +
                    token1MetaData?.symbol +
                    " LP",
                lpTotalSupply: Number(
                    ethers.formatUnits(
                        pairTotalSupply,
                        currencyMetadata?.decimals,
                    ),
                ),
                approvedBalance: Number(
                    ethers.formatUnits(
                        approvedAmount,
                        currencyMetadata?.decimals,
                    ),
                ),
                lpUserBalance: Number(
                    ethers.formatUnits(userBalance, currencyMetadata?.decimals),
                ),
                userShare: userShare,
                tokenOMetaData,
                token1MetaData,
                token0Name: tokenOMetaData?.name,
                token0Symbol: tokenOMetaData?.symbol,
                token0UserBalance:
                    (userShare *
                        Number(
                            ethers.formatUnits(
                                tokenOUserBalance,
                                tokenOMetaData?.decimals,
                            ),
                        )) /
                    100,
                token1Name: token1MetaData?.name,
                token1Symbol: token1MetaData?.symbol,
                token1UserBalance:
                    (userShare *
                        Number(
                            ethers.formatUnits(
                                token1UserBalance,
                                token1MetaData?.decimals,
                            ),
                        )) /
                    100,
            };
            console.log("%c Line:85 🍰 pairData", "color:#6ec1c2", pairData);
            setLpDetails(pairData);
            setDataLoading(false);
        } catch (error) {
            console.log("%c Line:47 🍉 error", "color:#42b983", error);
            setDataLoading(false);
        }
    };

    const approveTokenFun = async (tokenAddress) => {
        try {
            setLoading(true);
            const contract = getErc20Contract(tokenAddress, chain);
            const transaction = approve({
                contract,
                spender: routerAddress,
                amount: 1000,
            });
            console.log(
                "%c Line:117 🍢 transaction",
                "color:#ed9ec7",
                transaction,
                add,
            );
            const tx = await sendTx({ transaction, account: add });
            console.log("%c Line:118 🥔 tx", "color:#93c0a4", tx);
            toast.success("Token approved");
            setLoading(false);
            // Re-fetch LP details to update approved balance
            if (add?.address && chain && pairAddress) {
                fetchLpDetails(pairAddress, add.address, chain);
            }
        } catch (error) {
            toast.error(error?.message);
            setLoading(false);
            console.log("%c Line:111 🍫 error", "color:#ed9ec7", error);
        }
    };

    const [token0Amount, setToken0Amount] = useState(0);
    const [token1Amount, setToken1Amount] = useState(0);

    const updateValue = (value) => {
        let val = parseFloat(value);
        setValue(value);
        let tokenAmount = (val * lpDetails.token0UserBalance) / 100;
        setToken0Amount(tokenAmount);
        let tokenBAmount = (val * lpDetails.token1UserBalance) / 100;
        setToken1Amount(tokenBAmount);
    };

    // Calculate minimum amount with slippage
    const calculateMinAmount = (amountStr, decimals) => {
        try {
            const slippageMultiplier = (100 - slippage) / 100;
            const minAmount = parseFloat(amountStr) * slippageMultiplier;
            return ethers.parseUnits(safeFixedString(minAmount, decimals), decimals);
        } catch (error) {
            console.log("Error calculating min amount:", error);
            return "1"; // Fallback
        }
    };

    const removeLiquidity = async () => {
        try {
            if (value <= 0) {
                toast.error("Please Enter Valid amount");
                return;
            }
            setLoading(true);
            console.log("%c Line:236 🥕 value", "color:#b03734", value);
            const contract = getRouterContract(chain);

            const token0Decimals = lpDetails?.tokenOMetaData?.decimals;
            const token1Decimals = lpDetails?.token1MetaData?.decimals;

            let token1amount = ethers.parseUnits(
                safeFixedString(token0Amount, token0Decimals),
                token0Decimals,
            );

            let token2amount = ethers.parseUnits(
                safeFixedString(token1Amount, token1Decimals),
                token1Decimals,
            );

            let amount = (value * lpDetails.lpUserBalance) / 100;
            let amountPair = ethers.parseUnits(
                safeFixedString(amount, lpDetails?.currencyMetadata?.decimals),
                lpDetails?.currencyMetadata?.decimals,
            );
            const transaction = prepareContractCall({
                contract,
                method: "function removeLiquidity(address tokenA, address tokenB, uint256 liquidity, uint256 amountAMin, uint256 amountBMin, address to, uint256 deadline) returns (uint256 amountA, uint256 amountB)",
                params: [
                    lpDetails?.token0Address,
                    lpDetails?.token1Address,
                    amountPair,
                    calculateMinAmount(safeFixedString(token0Amount, token0Decimals), token0Decimals),
                    calculateMinAmount(safeFixedString(token1Amount, token1Decimals), token1Decimals),
                    address,
                    Math.floor(new Date().getTime() / 1000) + 900,
                ],
            });
            console.log(
                "%c Line:280 🧀 transaction",
                "color:#465975",
                transaction,
            );
            const txData = await sendTx({ transaction, account: add });
            console.log("%c Line:173 🥐 txData", "color:#3f7cff", txData);

            // Wait for the burn to be confirmed on-chain before Pool reads
            // the LP balance, otherwise stale balance can flash.
            await waitForReceipt({
                client: ThirdWebClient,
                chain,
                transactionHash: txData.transactionHash,
            });

            toast.success("Liquidity Removed Successfully");
            setLoading(false);

            // Navigate back to pool page with refresh state
            navigate('/pool', { state: { refresh: true } });
        } catch (error) {
            toast.error(error?.message);
            setLoading(false);
            console.log("%c Line:166 🍕 error", "color:#f5ce50", error);
        }
    };

    const Skeleton = ({ className }) => (
        <div className={`bg-perpx-dark/60 rounded animate-pulse ${className}`}></div>
    );

    return (
        <div className="max-w-[524px] mx-3 sm:mx-auto mt-10 flex gap-2 items-center flex-col p-5 rounded-xl bg-perpx-dark-tertiary/50 backdrop-blur-md border border-perpx-cyan/20">
            <div className="flex justify-between items-center px-2 w-full mb-2">
                <FaArrowLeft
                    className="size-[20px] text-perpx-cyan cursor-pointer hover:text-perpx-cyan/80 transition-all"
                    onClick={() => navigate('/pool')}
                />
                <h6 className="text-[18px] sm:text-[20px] text-perpx-cyan font-semibold">
                    Remove Liquidity
                </h6>
                <IoSettings
                    className="size-[24px] text-perpx-cyan cursor-pointer hover:text-perpx-cyan/80 transition-all"
                    onClick={() => setSettingsOpen(true)}
                    title="Slippage Settings"
                />
            </div>
            <div className="rounded-[16px] w-full bg-perpx-dark-secondary/80 p-4 border border-perpx-cyan/20">
                <div className="flex justify-between items-center px-2 w-full">
                    <h6 className="text-[14px] sm:text-[16px] text-perpx-text-primary font-semibold">
                        Remove Amount
                    </h6>
                    <h6 className="text-[14px] sm:text-[16px] text-perpx-cyan font-medium cursor-pointer hover:text-perpx-cyan/80 transition-all">
                        Detailed
                    </h6>
                </div>
                <p className="text-[76px] text-left text-perpx-cyan font-bold">
                    {value}%
                </p>
                <input
                    type="range"
                    min="1"
                    max="100"
                    value={value}
                    className="w-full outline-none slider"
                    disabled={dataLoading}
                    onChange={(e) => updateValue(e.target.value)}
                />
                <div className="flex justify-between items-center px-2 w-full mt-4">
                    <h6
                        onClick={() => !dataLoading && updateValue("25")}
                        className={`text-[14px] sm:text-[16px] font-semibold py-2 px-3 bg-perpx-dark/90 cursor-pointer rounded-[10px] border-2 transition-all ${
                            dataLoading ? "opacity-50 cursor-not-allowed" :
                            value === "25"
                                ? "border-perpx-cyan text-perpx-cyan shadow-perpx-glow"
                                : "border-perpx-cyan/30 text-perpx-text-secondary hover:border-perpx-cyan/50"
                        }`}
                    >
                        25%
                    </h6>
                    <h6
                        onClick={() => !dataLoading && updateValue("50")}
                        className={`text-[14px] sm:text-[16px] font-semibold py-2 px-3 bg-perpx-dark/90 cursor-pointer rounded-[10px] border-2 transition-all ${
                            dataLoading ? "opacity-50 cursor-not-allowed" :
                            value === "50"
                                ? "border-perpx-cyan text-perpx-cyan shadow-perpx-glow"
                                : "border-perpx-cyan/30 text-perpx-text-secondary hover:border-perpx-cyan/50"
                        }`}
                    >
                        50%
                    </h6>
                    <h6
                        onClick={() => !dataLoading && updateValue("75")}
                        className={`text-[14px] sm:text-[16px] font-semibold py-2 px-3 bg-perpx-dark/90 cursor-pointer rounded-[10px] border-2 transition-all ${
                            dataLoading ? "opacity-50 cursor-not-allowed" :
                            value === "75"
                                ? "border-perpx-cyan text-perpx-cyan shadow-perpx-glow"
                                : "border-perpx-cyan/30 text-perpx-text-secondary hover:border-perpx-cyan/50"
                        }`}
                    >
                        75%
                    </h6>
                    <h6
                        onClick={() => !dataLoading && updateValue("100")}
                        className={`text-[14px] sm:text-[16px] font-semibold py-2 px-3 bg-perpx-dark/90 cursor-pointer rounded-[10px] border-2 transition-all ${
                            dataLoading ? "opacity-50 cursor-not-allowed" :
                            value === "100"
                                ? "border-perpx-cyan text-perpx-cyan shadow-perpx-glow"
                                : "border-perpx-cyan/30 text-perpx-text-secondary hover:border-perpx-cyan/50"
                        }`}
                    >
                        Max
                    </h6>
                </div>
            </div>
            <FaArrowLeft className="size-[20px] -rotate-90 text-perpx-cyan my-[-8px] z-50" />
            <div className="rounded-[16px] w-full bg-perpx-dark-secondary/80 p-4 border border-perpx-cyan/20">
                <div className="flex items-center justify-between p-2 w-full">
                    <h6 className="text-perpx-cyan text-[18px] font-semibold">
                        {dataLoading ? <Skeleton className="w-16 h-5" /> : token0Amount.toFixed(3)}
                    </h6>
                    <div className="flex gap-2 items-center">
                        {dataLoading ? (
                            <Skeleton className="w-24 h-6" />
                        ) : (
                            <>
                                <TokenIcon symbol={lpDetails?.token0Symbol} size="20px" />
                                <p className="text-perpx-text-primary text-[20px] font-semibold">
                                    {lpDetails?.token0Symbol}
                                </p>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-between p-2 w-full">
                    <h6 className="text-perpx-cyan text-[18px] font-semibold">
                        {dataLoading ? <Skeleton className="w-16 h-5" /> : token1Amount.toFixed(3)}
                    </h6>
                    <div className="flex gap-2 items-center">
                        {dataLoading ? (
                            <Skeleton className="w-24 h-6" />
                        ) : (
                            <>
                                <TokenIcon symbol={lpDetails?.token1Symbol} size="20px" />
                                <p className="text-perpx-text-primary text-[20px] font-semibold">
                                    {lpDetails?.token1Symbol}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="w-full p-3 flex justify-between items-center bg-perpx-dark-secondary/80 rounded-[14px] border border-perpx-cyan/10">
                <p className="text-perpx-text-primary text-[16px] sm:text-[18px] font-semibold">
                    Price:
                </p>
                <div className="flex flex-col items-end p-1">
                    {dataLoading ? (
                        <>
                            <Skeleton className="w-32 h-4 mb-1" />
                            <Skeleton className="w-32 h-4" />
                        </>
                    ) : (
                        <>
                            <p className="text-perpx-cyan text-[14px] font-medium">
                                1 {lpDetails?.token0Symbol} ={" "}
                                {lpDetails?.token0Price?.toFixed(3)}{" "}
                                {lpDetails?.token1Symbol}
                            </p>
                            <p className="text-perpx-cyan text-[14px] font-medium">
                                1 {lpDetails?.token1Symbol} ={" "}
                                {lpDetails?.token1Price?.toFixed(3)}{" "}
                                {lpDetails?.token0Symbol}
                            </p>
                        </>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 w-full mt-4">
                {dataLoading ? (
                    <Skeleton className="w-full h-[52px] rounded-[64px]" />
                ) : lpDetails && lpDetails?.lpUserBalance <= 0 ? (
                    // Connected wallet holds no LP for this pair — sending the
                    // burn would revert with INSUFFICIENT_LIQUIDITY_BURNED.
                    <button
                        className="bg-perpx-cyan w-full rounded-[64px] py-[16px] px-[4px] text-perpx-dark text-[18px] font-semibold hover:shadow-perpx-glow-lg hover:scale-[1.02] transition-all"
                        onClick={() => navigate('/pool')}
                    >
                        No Liquidity to Remove — Back to Pool
                    </button>
                ) : lpDetails && lpDetails?.approvedBalance <= 0 ? (
                    loading ? (
                        <button
                            className="bg-perpx-cyan w-full rounded-[64px] py-[16px] px-[4px] text-perpx-dark text-[18px] font-semibold hover:shadow-perpx-glow-lg transition-all disabled:opacity-50"
                            disabled
                        >
                            Loading...
                        </button>
                    ) : (
                        <button
                            className="bg-perpx-cyan w-full rounded-[64px] py-[16px] px-[4px] text-perpx-dark text-[18px] font-semibold hover:shadow-perpx-glow-lg hover:scale-[1.02] transition-all"
                            onClick={() => {
                                approveTokenFun(lpDetails?.pairAddress);
                            }}
                        >
                            Approve
                        </button>
                    )
                ) : (
                    loading ? (
                        <button
                            className="bg-perpx-cyan w-full rounded-[64px] py-[16px] px-[4px] text-perpx-dark text-[18px] font-semibold hover:shadow-perpx-glow-lg transition-all disabled:opacity-50"
                            disabled
                        >
                            Loading...
                        </button>
                    ) : (
                        <button
                            className="bg-perpx-cyan w-full rounded-[64px] py-[16px] px-[4px] text-perpx-dark text-[18px] font-semibold hover:shadow-perpx-glow-lg hover:scale-[1.02] transition-all disabled:opacity-50"
                            disabled={!lpDetails?.pairAddress || value === 0 || token0Amount <= 0 || token1Amount <= 0}
                            onClick={removeLiquidity}
                        >
                            Remove Liquidity
                        </button>
                    )
                )}
            </div>
            <SlippageSettings
                open={settingsOpen}
                setOpen={setSettingsOpen}
                slippage={slippage}
                setSlippage={setSlippage}
            />
        </div>
    );
};

export default RemoveLiquidity;
